import * as React from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import {
  Paper, Box, Typography, Stack, Chip, Skeleton,
  Modal, Button, Divider, useTheme, Alert as MuiAlert,
  ButtonGroup, Slider
} from '@mui/material';
import ArrowUpwardRoundedIcon from '@mui/icons-material/ArrowUpwardRounded';
import ArrowDownwardRoundedIcon from '@mui/icons-material/ArrowDownwardRounded';
import HorizontalRuleRoundedIcon from '@mui/icons-material/HorizontalRuleRounded';
import PowerSettingsNewRoundedIcon from '@mui/icons-material/PowerSettingsNewRounded';
import SettingsRemoteRoundedIcon from '@mui/icons-material/SettingsRemoteRounded';

import { SparkLineChart } from '@mui/x-charts/SparkLineChart';
import { areaElementClasses } from '@mui/x-charts/LineChart';

import SmallConfigTemperatur from './SmallConfigTemperatur';
import SmallConfigLicht from './SmallConfigLicht';
import SmallConfigAbluft from './SmallConfigAbluft';

import { useGrowSetup } from '../GrowSetupContext';
import { useContext, useEffect, useMemo, useState } from 'react';
import { AuthContext } from './signin/AuthContext';
import { useAlert } from './useAlert';

/* ---------- Helpers ---------- */

function AreaGradient({ color, id }) {
  return (
    <defs>
      <linearGradient id={id} x1="50%" y1="0%" x2="50%" y2="100%">
        <stop offset="0%" stopColor={color} stopOpacity={0.28} />
        <stop offset="100%" stopColor={color} stopOpacity={0} />
      </linearGradient>
    </defs>
  );
}
AreaGradient.propTypes = { color: PropTypes.string.isRequired, id: PropTypes.string.isRequired };

function getStateMeta(isOn, theme) {
  return isOn
    ? { color: theme.palette.success.main, label: 'AN' }
    : { color: theme.palette.error.main, label: 'AUS' };
}
function getTrendMeta(trend, theme) {
  switch (trend) {
    case 'up': return { color: theme.palette.success.main, Icon: ArrowUpwardRoundedIcon, label: 'steigend' };
    case 'down': return { color: theme.palette.error.main, Icon: ArrowDownwardRoundedIcon, label: 'fallend' };
    default: return { color: theme.palette.text.secondary, Icon: HorizontalRuleRoundedIcon, label: 'stabil' };
  }
}
function borderTint(isOn, theme) {
  const c = isOn ? theme.palette.success.main : theme.palette.error.main;
  return {
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: `inset 0 0 0 2px ${c}22`,
    background: theme.palette.mode === 'light' ? `${c}0D` : `${c}26`,
  };
}

/* ---------- Component ---------- */

function StatCard(props) {
  const theme = useTheme();
  const { grow_setup } = useGrowSetup();
  const { token } = useContext(AuthContext);
  const showAlert = useAlert();

  const [datas, setData] = useState([]);
  const [xaxis, setXaxis] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [err, setErr] = useState(null);

  const [open, setOpen] = useState(false);

  const [actorIsOn, setActorIsOn] = useState(false);
  const [currentMode, setCurrentMode] = useState('auto'); // 'auto' | 'on' | 'off'
  const [intensityValue, setIntensityValue] = useState(0); // 0..100

  const gradientId = useMemo(() => `area-gradient-${props.measure}-${props.value}`, [props.measure, props.value]);
  const trendMeta = getTrendMeta(props.trend ?? 'neutral', theme);
  const stateMeta = getStateMeta(actorIsOn, theme);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  /* ---------- Data Fetch ---------- */

  useEffect(() => {
    if (!grow_setup) return;
    let cancelled = false;
    const controller = new AbortController();

    const fetchAll = async () => {
      setIsLoading(true);
      setErr(null);
      try {
        const [measuresRes, actorRes] = await Promise.all([
          axios.post(
            '/get_measures_by_timedelta/',
            {
              start_time: new Date(Date.now() - 24 * 60 * 60 * 1000),
              end_time: new Date(),
              grow_setup_id: grow_setup,
              aggregation_level: '5m',
              measure: props.measure,
            },
            {
              headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
              },
              signal: controller.signal,
            }
          ),
          axios.post(
            '/get_actor_data',
            { grow_setup_id: grow_setup, measure: props.measure },
            {
              headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
              },
              signal: controller.signal,
            }
          ),
        ]);

        if (cancelled) return;

        // chart data
        const row = measuresRes?.data?.[0];
        const data = row?.data ?? [];
        const rawX = row?.xaxis ?? [];
        const pad = (n) => String(n).padStart(2, '0');
        const fmt = (ts) => {
          const dt = new Date(ts);
          return `${pad(dt.getHours())}:${pad(dt.getMinutes())}`;
        };
        setData(data);
        setXaxis(rawX.map(fmt));

        // actor state
        if (actorRes?.data) {
          const m = actorRes.data.mode;           // 'auto' | 'on' | 'off'
          const v = Number(actorRes.data.value);  // 0..100
          setCurrentMode(m);
          setActorIsOn(m === 'on');
          setIntensityValue(Number.isFinite(v) ? v : 0);
        }
      } catch (e) {
        if (!cancelled) {
          console.error(e);
          setErr('Daten konnten nicht geladen werden.');
          setData([]);
          setXaxis([]);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    fetchAll();
    return () => {
      cancelled = true;
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [grow_setup, props.measure]);

  /* ---------- Actions (keep functionality) ---------- */

  const saveActorIntensity = async (value) => {
    try {
      await axios.post(
        '/set_actor_intensity',
        { id: grow_setup, actor: props.measure, intensity: value },
        {
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setActorIsOn(true);
      showAlert('Actor-Intensität erfolgreich geändert', 'success');
    } catch (e) {
      console.error(e);
      showAlert('Actor-Intensität konnte nicht geändert werden', 'error');
    }
  };

  const handleModeChange = async (mode) => {
    try {
      await axios.post(
        '/set_actor_mode',
        { id: grow_setup, actor: props.measure, mode },
        {
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setCurrentMode(mode);
      setActorIsOn(mode === 'on');
      showAlert('Actor-Modus erfolgreich geändert', 'success');
    } catch (e) {
      console.error(e);
      showAlert('Actor-Modus konnte nicht geändert werden', 'error');
    }
  };

  /* ---------- Render ---------- */

  return (
    <Paper
      elevation={theme.palette.mode === 'light' ? 8 : 1}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 3,
        overflow: 'hidden',
        ...borderTint(actorIsOn || currentMode === 'auto', theme),
      }}
    >
      {/* Header */}
      <Box sx={{ px: 2, pt: 2, pb: 1 }}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={2}>
          <Stack spacing={0.25}>
            <Typography variant="subtitle2" color="text.secondary">
              {props.title}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              {!props.is_device && (
                <Chip
                  size="small"
                  icon={<SettingsRemoteRoundedIcon />}
                  label={actorIsOn ? `Maximale Intensität ${intensityValue}%` : 'Maximale Intensität —'}
                  variant="outlined"
                />
              )}
            </Stack>
          </Stack>

          <Stack alignItems="flex-end" spacing={0.25}>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Typography variant="h6" sx={{ whiteSpace: 'nowrap' }}>
                {props.gauge_value == null ? 'N/A' : `${props.gauge_value}${props.einheit_zeichen ?? ''}`}
              </Typography>
              <trendMeta.Icon fontSize="small" sx={{ color: trendMeta.color }} />
            </Stack>
            {props.target_hint && (
              <Typography variant="caption" color="text.secondary">{props.target_hint}</Typography>
            )}
          </Stack>
        </Stack>
      </Box>

      <Divider sx={{ opacity: 0.5 }} />

      {/* Controls */}
      {!props.is_device && (
        <Box sx={{ px: 2, pt: 1 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center" justifyContent="space-between">
            <ButtonGroup size="small" variant="outlined">
              <Button
                variant={currentMode === 'auto' ? 'contained' : 'outlined'}
                onClick={() => handleModeChange('auto')}
              >
                Auto
              </Button>
              <Button
                variant={currentMode === 'on' ? 'contained' : 'outlined'}
                onClick={() => handleModeChange('on')}
              >
                On
              </Button>
              <Button
                variant={currentMode === 'off' ? 'contained' : 'outlined'}
                onClick={() => handleModeChange('off')}
              >
                Off
              </Button>
            </ButtonGroup>

            <Box sx={{ flex: 1, minWidth: 160 }}>
              <Slider
                value={intensityValue}
                min={0}
                max={100}
                step={5}
                marks
                valueLabelDisplay="auto"
                disabled={!actorIsOn}
                onChange={(_, v) => setIntensityValue(Number(v))}
                onChangeCommitted={(_, v) => saveActorIntensity(Number(v))}
              />
            </Box>
          </Stack>
        </Box>
      )}

      {/* Chart */}
      <Box sx={{ px: 2, py: 1.5, flex: 1, minHeight: 90 }}>
        {isLoading ? (
          <Skeleton variant="rounded" height={60} />
        ) : err ? (
          <MuiAlert severity="error" variant="outlined">{err}</MuiAlert>
        ) : (
          <Box sx={{ width: '100%', height: 60 }}>
            <SparkLineChart
              data={datas}
              xAxis={{ scaleType: 'band', data: xaxis }}
              colors={[trendMeta.color]}
              area
              showHighlight
              showTooltip
              sx={{
                [`& .${areaElementClasses.root}`]: { fill: `url(#${gradientId})` },
                '& .MuiChartsAxis-root': { display: 'none' },
              }}
            >
              <AreaGradient color={trendMeta.color} id={gradientId} />
            </SparkLineChart>
          </Box>
        )}
      </Box>

      {/* Actions */}
      <Divider sx={{ mt: 'auto', opacity: 0.5 }} />
      <Box sx={{ px: 2, py: 1, display: 'flex', justifyContent: 'flex-end' }}>
        <Button size="small" onClick={handleOpen}>Details anzeigen</Button>
      </Box>

      {/* Modal */}
      <Modal open={open} onClose={handleClose}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 420,
            maxWidth: '90vw',
            bgcolor: 'background.paper',
            borderRadius: 3,
            boxShadow: 24,
            p: 2,
          }}
        >
          {props.measure === 'licht' && <SmallConfigLicht />}
          {props.measure === 'abluft' && <SmallConfigAbluft />}
          {props.measure === 'temperatur' && <SmallConfigTemperatur />}
          {/* Falls du CO2-Aktor hast: hier ergänzen */}
        </Box>
      </Modal>
    </Paper>
  );
}

/* ---------- PropTypes ---------- */

StatCard.propTypes = {
  value: PropTypes.string,                        // Chart-ID/Key
  measure: PropTypes.string.isRequired,           // 'licht' | 'abluft' | 'temperatur' ...
  gauge_value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  einheit_zeichen: PropTypes.string,
  trend: PropTypes.oneOf(['down', 'neutral', 'up']),
  title: PropTypes.string,
  target_hint: PropTypes.string,
  is_device: PropTypes.bool,                      // blendet Controls aus, wenn true
};

export default React.memo(StatCard);
